import StockUpdatedAt from '../components/StockUpdatedAt';
import { DriveFileImport } from '../components/sync/DriveSource';
import { useAuth } from '../contexts/AuthContext';
import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsAPI, ficAPI, wmsAPI } from '../utils/api';
import { Prodotto, WmsConnectionStatus } from '../types';
import { useWmsStockBatch } from '../hooks/useWmsStock';
import { usePermissions } from '../hooks/usePermissions';
import StockBadge from '../components/StockBadge';
import { productDisplayPrice } from '../utils/productPrice';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  Trash2,
  Package,
  Wrench,
  Tag,
  Loader2,
  DownloadCloud,
  ImageDown,
} from 'lucide-react';

interface MetelConflict {
  codice_metel: string;
  detail: string;
}

interface MetelImportReport {
  mode: 'listino' | 'immagini';
  processed: number;
  matched: number;
  updated: number;
  images_cached: number;
  conflicts: MetelConflict[];
}

export default function Products() {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  const driveTenantId = tenant?.id || user?.tenant_id;
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('prodotti:create');
  const canEdit = hasPermission('prodotti:edit');
  const canDelete = hasPermission('prodotti:delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState<string>('');
  const [marcaFilter, setMarcaFilter] = useState<string>('');
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const [statoFilter, setStatoFilter] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [metelReport, setMetelReport] = useState<MetelImportReport | null>(null);
  const listinoMetelInput = useRef<HTMLInputElement>(null);
  const immaginiMetelInput = useRef<HTMLInputElement>(null);

  // Fetch FIC status
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: async () => {
      const response = await ficAPI.getStatus();
      return response.data;
    },
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const response = await productsAPI.getCategories();
      return response.data;
    },
  });

  // Fetch brands
  const { data: brands } = useQuery({
    queryKey: ['product-brands'],
    queryFn: async () => {
      const response = await productsAPI.getBrands();
      return response.data;
    },
  });

  // Fetch products
  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products', page, search, categoriaFilter, marcaFilter, tipoFilter, statoFilter],
    queryFn: async () => {
      const response = await productsAPI.getProducts({
        page,
        limit: 20,
        search: search || undefined,
        categoria: categoriaFilter || undefined,
        marca: marcaFilter || undefined,
        is_servizio: tipoFilter === '' ? undefined : tipoFilter === 'servizio',
        is_attivo: statoFilter === '' ? undefined : statoFilter === 'attivo',
      });
      return response.data;
    },
  });

  // Delete product mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => productsAPI.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Prodotto eliminato con successo');
      setDeleteConfirm(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    },
  });

  // Import from FIC mutation
  const importMutation = useMutation({
    mutationFn: () => productsAPI.importFromFIC(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-categories'] });
      toast.success(response.data.message);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione');
    },
  });

  const metelMutation = useMutation({
    mutationFn: ({ file, mode }: { file: File; mode: 'listino' | 'immagini' }) => productsAPI.importMetel(file, mode),
    onSuccess: (response) => {
      const report: MetelImportReport = response.data;
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setMetelReport(report);
      toast.success(`METEL: ${report.matched} associati, ${report.images_cached} immagini salvate, ${report.conflicts.length} anomalie`);
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Errore nell\'import METEL'),
  });

  const selectMetelFile = (mode: 'listino' | 'immagini', file?: File) => {
    if (file) metelMutation.mutate({ file, mode });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  // Fetch WMS status to know if integration is active
  const { data: wmsStatus } = useQuery<WmsConnectionStatus>({
    queryKey: ['wms-status'],
    queryFn: async () => {
      const response = await wmsAPI.getStatus();
      return response.data;
    },
  });

  const products = productsData?.data || [];
  const pagination = productsData?.pagination;

  const productIds = useMemo(() => products.map((p: Prodotto) => p.id), [products]);
  const { data: stockMap, isPending: loadingStock, isError: stockError } = useWmsStockBatch(wmsStatus?.is_active ? productIds : []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prodotti e Servizi</h1>
          <p className="text-gray-600">Gestisci il catalogo prodotti e servizi</p>
        </div>
        <div className="flex gap-3">
          {canEdit && <>
            <input ref={listinoMetelInput} type="file" accept=".txt,.metel,text/plain" className="hidden" onChange={(e) => selectMetelFile('listino', e.target.files?.[0])} />
            <input ref={immaginiMetelInput} type="file" accept=".txt,.metel,text/plain" className="hidden" onChange={(e) => selectMetelFile('immagini', e.target.files?.[0])} />
            <button onClick={() => listinoMetelInput.current?.click()} disabled={metelMutation.isPending} className="flex items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-50">
              <DownloadCloud className="h-5 w-5" /> Importa listino METEL
            </button>
            <button onClick={() => immaginiMetelInput.current?.click()} disabled={metelMutation.isPending} className="flex items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-50">
              <ImageDown className="h-5 w-5" /> Importa immagini METEL
            </button>
          </>}
          {ficStatus?.connected && (
            <button
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {importMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <DownloadCloud className="h-5 w-5" />
              )}
              Importa da FIC
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => navigate('/products/new')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
              Nuovo Prodotto
            </button>
          )}
        </div>
      </div>

      {canEdit && driveTenantId && <DriveFileImport key={driveTenantId} tenantId={driveTenantId} />}

      {metelReport && (
        <div className={`rounded-lg border p-4 ${metelReport.conflicts.length ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-semibold text-gray-900">
                Import {metelReport.mode === 'listino' ? 'listino' : 'immagini'} METEL completato
              </h2>
              <p className="mt-1 text-sm text-gray-700">
                {metelReport.processed} record letti · {metelReport.matched} prodotti associati
                {metelReport.updated > 0 && ` · ${metelReport.updated} prezzi aggiornati`}
                {metelReport.images_cached > 0 && ` · ${metelReport.images_cached} immagini salvate`}
              </p>
            </div>
            <button onClick={() => setMetelReport(null)} className="text-sm font-medium text-gray-600 hover:text-gray-900">Chiudi</button>
          </div>
          {metelReport.conflicts.length > 0 && (
            <div className="mt-3 overflow-x-auto rounded border border-amber-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-amber-100 text-left text-amber-950"><tr><th className="px-3 py-2 font-medium">Codice METEL</th><th className="px-3 py-2 font-medium">Anomalia</th></tr></thead>
                <tbody className="divide-y divide-amber-100">
                  {metelReport.conflicts.map((conflict, index) => (
                    <tr key={`${conflict.codice_metel}-${index}`}><td className="px-3 py-2 font-mono text-gray-900">{conflict.codice_metel}</td><td className="px-3 py-2 text-gray-700">{conflict.detail}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome, codice..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={categoriaFilter}
            onChange={(e) => {
              setCategoriaFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutte le categorie</option>
            {categories?.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <select
            value={marcaFilter}
            onChange={(e) => {
              setMarcaFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutte le marche</option>
            {brands?.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
          <select
            value={tipoFilter}
            onChange={(e) => {
              setTipoFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti i tipi</option>
            <option value="prodotto">Prodotti</option>
            <option value="servizio">Servizi</option>
          </select>
          <select
            value={statoFilter}
            onChange={(e) => {
              setStatoFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="attivo">Attivi</option>
            <option value="inattivo">Inattivi</option>
          </select>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun prodotto trovato</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prodotto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marca
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prezzo
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IVA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margine
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                {wmsStatus?.is_active && (
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Disp.
                  </th>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product: Prodotto) => {
                const price = productDisplayPrice(product);
                return (
                <tr
                  key={product.id}
                  onClick={canEdit ? () => navigate(`/products/${product.id}/edit`) : undefined}
                  className={canEdit ? 'hover:bg-gray-50 cursor-pointer' : ''}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {product.immagine_default_url ? (
                          <img
                            src={product.immagine_default_url}
                            alt={product.nome}
                            className="w-10 h-10 object-cover"
                            loading="lazy"
                          />
                        ) : product.is_servizio ? (
                          <Wrench className="h-5 w-5 text-purple-400" />
                        ) : (
                          <Package className="h-5 w-5 text-blue-400" />
                        )}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {product.nome}
                        </div>
                        <div className="text-sm text-gray-500">
                          {product.codice && `Cod: ${product.codice}`}
                          {product.codice && product.unita_misura && ' · '}
                          {product.unita_misura && `UM: ${product.unita_misura}`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.categoria && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <Tag className="h-3 w-3 mr-1" />
                        {product.categoria}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.marca && (
                      <span className="text-sm text-gray-700">
                        {product.marca}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {price.amount !== null
                        ? formatCurrency(price.amount)
                        : 'Prezzo non configurato'}
                    </div>
                    {price.amount !== null && (
                      <div className="text-xs text-gray-500">{price.source}</div>
                    )}
                    {product.prezzo_acquisto !== undefined && product.prezzo_acquisto !== null && (
                      <div className="text-xs text-gray-500">
                        Costo: {formatCurrency(product.prezzo_acquisto)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {product.aliquota_iva}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {product.margine > 0 ? (
                      <span className={`text-sm font-medium ${product.margine >= 30 ? 'text-green-600' : product.margine >= 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {product.margine.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {product.is_attivo ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inattivo
                      </span>
                    )}
                  </td>
                  {wmsStatus?.is_active && (
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {product.is_servizio ? <span className="text-gray-400">—</span> : loadingStock ? (
                        <span className="text-xs text-gray-500">Caricamento…</span>
                      ) : stockError ? (
                        <span className="text-xs text-gray-500">Giacenza non disponibile</span>
                      ) : (
                        <>
                          <StockBadge stockInfo={stockMap?.get(product.id)} />
                          <StockUpdatedAt stock={stockMap?.get(product.id)} />
                        </>
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(product.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} prodotti)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Conferma Eliminazione</h3>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler eliminare questo prodotto? L'operazione non può essere annullata.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Elimina'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
